import type { SettlementViewModel } from '@aztec-realms/shared-types';

import {
  environmentAssetManifest,
  resolveSettlementBuildingSprite,
  resolveSettlementConstructionAccessory,
  resolveSettlementConstructionArt,
  settlementDetailSpriteManifest,
  settlementWaterFoundationManifest,
} from './asset-manifest';
import { constructionStageIds } from './construction-stage';
import { settlementExpansionGroundManifest } from './settlement-expansion-ground';
import { settlementBridgeDeckDelivery } from './settlement-bridge-delivery';
import { settlementBridgePostManifest } from './settlement-bridge-art';
import { settlementPathMaterial } from './settlement-path-material';

export interface SettlementRequiredArt {
  id: string;
  url: string;
  /** Try the retained master once if an optimized delivery file fails. */
  fallbackUrl?: string;
  frameWidth?: number;
  frameHeight?: number;
}

/** Owned architecture, its pending construction phases, and unlocked terrain only. */
export function requiredSettlementArt(
  snapshot: SettlementViewModel,
  preview?: { buildingId: string; orientation?: number },
): SettlementRequiredArt[] {
  const assets: SettlementRequiredArt[] = [
    environmentAssetManifest.settlement,
    settlementDetailSpriteManifest.banner,
    settlementDetailSpriteManifest.canoe,
  ];
  const constructing = snapshot.buildings.some((building) => building.state === 'constructing');
  if (
    snapshot.districts.some(
      (district) =>
        district.unlocked &&
        snapshot.buildings.filter((building) => building.districtCode === district.code).length > 1,
    )
  )
    assets.push(settlementPathMaterial);
  if (constructing || (snapshot.ambientPopulation > 0 && snapshot.buildings.length > 0))
    assets.push(settlementDetailSpriteManifest.resident);
  for (const building of snapshot.buildings) {
    const level =
      building.state === 'constructing'
        ? (building.constructionTargetLevel ?? building.level)
        : building.level;
    const orientation =
      preview?.buildingId === building.id
        ? (preview.orientation ?? building.orientation)
        : building.orientation;
    const completed = resolveSettlementBuildingSprite(
      building.type,
      'completed',
      level,
      orientation,
    );
    if (completed) assets.push(completed);
    if (building.state !== 'constructing') continue;
    // Clock-derived phases can change before another server snapshot arrives. Keep all
    // phases of this owned project ready, including finishing supplies and activation art.
    for (const stage of constructionStageIds) {
      const art = resolveSettlementConstructionArt(building.type, stage, level, orientation);
      const accessory = resolveSettlementConstructionAccessory(stage, orientation);
      if (art) assets.push(art);
      if (accessory) assets.push(accessory);
    }
  }
  if (
    snapshot.buildings.some((building) =>
      settlementWaterFoundationManifest.buildingTypes.some((type) => type === building.type),
    )
  )
    assets.push(settlementWaterFoundationManifest);
  for (const district of snapshot.districts) {
    const ground = Object.entries(settlementExpansionGroundManifest).find(
      ([code]) => code === district.code,
    )?.[1];
    if (district.unlocked && ground) assets.push(ground);
  }
  const bridge = settlementBridgeDeckDelivery(snapshot.districts);
  if (bridge) assets.push(bridge, settlementBridgePostManifest, settlementPathMaterial);
  return [...new Map(assets.map((asset) => [asset.id, asset])).values()];
}
